import { PDFDocument } from "pdf-lib";
import { saveBlobAsFile } from "../../../services/api/editor/export";
import {
  computePassportSheetGrid,
  getPassportSheetSize,
} from "../AiPhotoStudio/aiPassportPhotoMaker/config/passportPhotoMakerFeatures";

const MM_TO_PT = 72 / 25.4;

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error("Could not load passport photo for print sheet."));
    img.src = src;
  });
}

async function imageUrlToPngBytes(imageUrl) {
  const img = await loadImageElement(imageUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || 1;
  canvas.height = img.naturalHeight || 1;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable for print sheet.");
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Could not encode photo."))),
      "image/png"
    );
  });
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Build and download a print-ready passport sheet as PDF.
 */
export async function downloadPassportPrintSheetPdf({
  imageUrl,
  sheetSizeId = "A4",
  photoWidthMm = 35,
  photoHeightMm = 45,
  fileName = "passport-print-sheet.pdf",
}) {
  if (!imageUrl) throw new Error("No passport photo for print sheet.");

  const sheet = getPassportSheetSize(sheetSizeId) || {
    id: "A4",
    widthMm: 210,
    heightMm: 297,
  };
  const grid = computePassportSheetGrid({
    sheetWidthMm: sheet.widthMm,
    sheetHeightMm: sheet.heightMm,
    photoWidthMm,
    photoHeightMm,
  });

  if (!grid.count) {
    throw new Error("Could not layout print sheet for this photo size.");
  }

  const pageWidthPt = grid.sheetWidthMm * MM_TO_PT;
  const pageHeightPt = grid.sheetHeightMm * MM_TO_PT;
  const photoWPt = grid.photoWidthMm * MM_TO_PT;
  const photoHPt = grid.photoHeightMm * MM_TO_PT;
  const marginPt = grid.marginMm * MM_TO_PT;
  const gutterPt = grid.gutterMm * MM_TO_PT;

  const pdf = await PDFDocument.create();
  const page = pdf.addPage([pageWidthPt, pageHeightPt]);
  const pngBytes = await imageUrlToPngBytes(imageUrl);
  const embedded = await pdf.embedPng(pngBytes);

  for (let row = 0; row < grid.rows; row += 1) {
    for (let col = 0; col < grid.cols; col += 1) {
      const x = marginPt + col * (photoWPt + gutterPt);
      const y =
        pageHeightPt - marginPt - photoHPt - row * (photoHPt + gutterPt);
      page.drawImage(embedded, {
        x,
        y,
        width: photoWPt,
        height: photoHPt,
      });
    }
  }

  const bytes = await pdf.save();
  const blob = new Blob([bytes], { type: "application/pdf" });
  saveBlobAsFile(blob, fileName);
  return blob;
}
